import { calculateSavings } from './savings-calculator';
import {
  defaultFleetDetails,
  defaultFuel,
  defaultMaintenance,
  defaultRoadCalls,
  defaultTires,
  getDefaultTireStyle,
  setCostOfTire,
  setTireReplacementCost,
} from './state';

export const mutationTypes = {
  SET_CURRENT_STEP: 'SET_CURRENT_STEP',
  SET_PREVIOUS_STEP: 'SET_PREVIOUS_STEP',
  SET_NEXT_STEP: 'SET_NEXT_STEP',
  SET_FLEET_DETAILS_VALUES: 'SET_FLEET_DETAILS_VALUES',
  SET_TIRES_VALUES: 'SET_TIRES_VALUES',
  SET_FUEL_VALUES: 'SET_FUEL_VALUES',
  SET_MAINTENANCE_VALUES: 'SET_MAINTENANCE_VALUES',
  SET_ROAD_CALLS: 'SET_ROAD_CALLS',
  RESET_VALUES: 'RESET_VALUES',
  SET_INITIAL_CALCULATION: 'SET_INITIAL_CALCULATION',
};

/**
 *
 * @param {keyof mutationTypes} mutationType
 */
export function mutation(mutationType) {
  return `roiCalculator/${mutationType}`;
}

/**
 * @type {import('vuex').MutationTree<ROICalculatorState>}
 */
export const mutations = {
  /**
   * @param {ROICalculatorState} state
   */
  [mutationTypes.SET_INITIAL_CALCULATION](state) {
    state.calculation = calculateSavings(state);
  },

  /**
   * @param {ROICalculatorState} state
   * @param {Step} step
   */
  [mutationTypes.SET_CURRENT_STEP](state, step) {
    state.currentStep = step;
  },

  /**
   * @param {ROICalculatorState} state
   */
  [mutationTypes.SET_PREVIOUS_STEP](state) {
    const currentStepIndex = state.steps.findIndex(
      (s) => s === state.currentStep,
    );

    if (currentStepIndex < 1) {
      return;
    }

    state.currentStep = state.steps[currentStepIndex - 1];
  },

  /**
   * @param {ROICalculatorState} state
   */
  [mutationTypes.SET_NEXT_STEP](state) {
    const currentStepIndex = state.steps.findIndex(
      (s) => s === state.currentStep,
    );

    if (
      currentStepIndex === -1 ||
      currentStepIndex === state.steps.length - 1
    ) {
      return;
    }

    state.currentStep = state.steps[currentStepIndex + 1];
  },

  /**
   * @param {ROICalculatorState} state
   * @param {FleetDetails} fleetDetails
   */
  [mutationTypes.SET_FLEET_DETAILS_VALUES](state, fleetDetails) {
    state.formValues.fleetDetails = fleetDetails;

    state.calculation = calculateSavings(state);
  },

  /**
   * @param {ROICalculatorState} state
   * @param {Tires} tires
   */
  [mutationTypes.SET_TIRES_VALUES](state, tires) {
    if (state.formValues.tires.tireStyle !== tires.tireStyle) {
      setCostOfTire(tires, tires.tireStyle);
    }

    setTireReplacementCost(state.formValues.roadCalls, tires);

    state.formValues.tires = tires;

    state.calculation = calculateSavings(state);
  },

  /**
   * @param {ROICalculatorState} state
   * @param {Fuel} fuel
   */
  [mutationTypes.SET_FUEL_VALUES](state, fuel) {
    state.formValues.fuel = fuel;

    state.calculation = calculateSavings(state);
  },

  /**
   * @param {ROICalculatorState} state
   * @param {Maintenance} maintenance
   */
  [mutationTypes.SET_MAINTENANCE_VALUES](state, maintenance) {
    state.formValues.maintenance = maintenance;

    state.calculation = calculateSavings(state);
  },

  /**
   * @param {ROICalculatorState} state
   * @param {RoadCalls} roadCalls
   */
  [mutationTypes.SET_ROAD_CALLS](state, roadCalls) {
    state.formValues.roadCalls = roadCalls;

    state.calculation = calculateSavings(state);
  },

  /**
   * @param {ROICalculatorState} state
   */
  [mutationTypes.RESET_VALUES](state) {
    const defaultTireStyle = getDefaultTireStyle();

    state.formValues.fleetDetails = defaultFleetDetails();
    state.formValues.tires = defaultTires(defaultTireStyle);
    state.formValues.fuel = defaultFuel();
    state.formValues.maintenance = defaultMaintenance();
    state.formValues.roadCalls = defaultRoadCalls(defaultTireStyle);

    state.calculation = calculateSavings(state);
  },
};

/**
 * @typedef {import('./state').ROICalculatorState} ROICalculatorState
 * @typedef {import('./state').Step} Step
 * @typedef {import('./state').FleetDetails} FleetDetails
 * @typedef {import('./state').Tires} Tires
 * @typedef {import('./state').Fuel} Fuel
 * @typedef {import('./state').Maintenance} Maintenance
 * @typedef {import('./state').RoadCalls} RoadCalls
 */
